import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useChecklistStore } from '../../store/checklistStore';

interface EvaluationInviteNoteProps {
  structureId: string;
  /** Route carries an insurer deep link's `?model=` param. */
  hasModelParam: boolean;
}

/**
 * Quiet arrival note for a visitor who followed an evaluation link — an
 * insurer's `?model=` deep link, or a property held in uiStore's
 * pendingProperty from the try-then-save preview flow. Sits directly under
 * AddressHeader so the invite is the first thing read on the page, ahead of
 * the featured checklist.
 */
export function EvaluationInviteNote({ structureId, hasModelParam }: EvaluationInviteNoteProps) {
  const { colors } = useTheme();
  const pendingStructureId = useUIStore((s) => s.pendingProperty?.structureId);
  const selectedChecklist = useChecklistStore((s) => s.selectedChecklist);
  const structureProgress = useChecklistStore((s) => s.structureProgress);
  const structureProgressKey = useChecklistStore((s) => s.structureProgressKey);

  const show = hasModelParam || pendingStructureId === structureId;
  if (!show) return null;

  // Same "is this actually loaded for this structure and model" guard
  // PropertyChecklistSection uses — an unset key would otherwise read as
  // zero progress rather than not-yet-known.
  const localLoaded =
    selectedChecklist != null && structureProgressKey === `${structureId}_${selectedChecklist.id}`;
  const hasProgress = localLoaded && (structureProgress?.completedItems.length ?? 0) > 0;

  return (
    <View style={[styles.card, { borderColor: colors.border }]} testID="structure-invite-note">
      <Text style={[styles.text, { color: colors.textSecondary }]}>
        You've been asked to evaluate this property.
      </Text>
      {hasProgress && (
        <Text style={[styles.text, { color: colors.textSecondary }]}>
          Continue evaluating this property.
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.smd,
    gap: SPACING.xs,
  },
  text: {
    ...TYPOGRAPHY.bodySmall,
  },
});
